import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { billingApi } from '../api/services';
import type { BillingInterval, BillingProduct } from '../types/models';
import { formatPence } from '@/lib/billing';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  AuthShell,
  AUTH_FIELD_CLASS,
  AUTH_FOOTNOTE_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_PRIMARY_BUTTON,
  AUTH_SUBTITLE_CLASS,
  AUTH_TITLE_CLASS,
} from '@/components/auth/AuthShell';

// ---------------------------------------------------------------------------
// SignupPage — Stratium-branded self-service signup. The flow is:
//
//   1. User picks a plan, monthly or yearly billing (when the plan has
//      a yearly price) and a seat count (not for a one-person plan such
//      as Personal), and supplies their contact details. The marketing
//      site deep-links here as /signup?plan=personal&interval=year.
//   2. Submit POSTs to `/api/billing/checkout` which creates a Stripe
//      Checkout Session (with a 14-day trial) and returns `checkoutUrl`.
//   3. We redirect the browser to Stripe Checkout, where the user
//      enters their card details.
//   4. On successful payment Stripe fires `checkout.session.completed`
//      to `/api/billing/webhook`, which provisions the tenant, the
//      owner user, seeds defaults, and emails the user a "set your
//      password" link.
//   5. Stripe's success_url drops the user on `/onboarding/success`
//      with the session id, where they're told to check their email.
//
// We don't collect a password here — the password is set via the
// emailed reset link after the webhook lands. We also don't apply
// any tokens; the user signs in once their account is provisioned
// and they've set their password.
// ---------------------------------------------------------------------------

function isWorkEmailish(email: string): boolean {
  // Cheap signal — full validation happens server-side. We just want
  // to catch the obvious gmail/yahoo/outlook personal addresses with
  // a calm inline note rather than a server roundtrip.
  const PERSONAL = ['gmail.com', 'googlemail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 'icloud.com', 'me.com', 'aol.com'];
  const dom = email.split('@')[1]?.toLowerCase() ?? '';
  return !!dom && !PERSONAL.includes(dom);
}

/** Whether the plan can be bought yearly right now. */
function offersYearly(p: BillingProduct | null): p is BillingProduct & { annualPricePencePerSeat: number } {
  return !!p && p.annualAvailable && !!p.annualPricePencePerSeat;
}

/** The plan card's price for the chosen interval: "£10 / month", "£29 / seat / month". */
function planPrice(p: BillingProduct, interval: BillingInterval): { price: string; unit: string } {
  if (p.isCustomPricing) return { price: 'Custom', unit: '' };
  const yearly = interval === 'year' && offersYearly(p);
  const pence = yearly ? p.annualPricePencePerSeat! : p.pricePerSeatPence;
  return {
    price: formatPence(pence, p.currency),
    unit: `${p.isSelfServeIndividual ? '' : ' / seat'} / ${yearly ? 'year' : 'month'}`,
  };
}

/** The interval toggle's copy: "£10 a month" / "£90 a year (£7.50 a month)". */
function intervalCopy(p: BillingProduct & { annualPricePencePerSeat: number }, interval: BillingInterval): string {
  const perSeat = p.isSelfServeIndividual ? '' : ' per seat';
  if (interval === 'month') return `${formatPence(p.pricePerSeatPence, p.currency)}${perSeat} a month`;
  const annual = p.annualPricePencePerSeat;
  return `${formatPence(annual, p.currency)}${perSeat} a year (${formatPence(Math.round(annual / 12), p.currency)} a month)`;
}

/** ?interval=year|yearly|annual → yearly; anything else is monthly. */
function intervalFromQuery(value: string | null): BillingInterval {
  return ['year', 'yearly', 'annual'].includes((value ?? '').trim().toLowerCase()) ? 'year' : 'month';
}

export default function SignupPage() {
  const [searchParams] = useSearchParams();

  const [plans, setPlans] = useState<BillingProduct[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  // The deep-link (?plan=personal&interval=year) is read once, on mount.
  const [query] = useState(() => ({
    plan: searchParams.get('plan')?.trim().toLowerCase() ?? '',
    interval: intervalFromQuery(searchParams.get('interval')),
  }));
  const [billingInterval, setBillingInterval] = useState<BillingInterval>(query.interval);
  const [seatCount, setSeatCount] = useState<number>(1);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailHint, setEmailHint] = useState('');

  // Document title sets a clean "Start your trial · Cadence" while the
  // surface is mounted; restore on unmount.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Start your trial · Cadence';
    return () => { document.title = prev; };
  }, []);

  // Load available plans on mount. Public endpoint, cached server-side
  // for 5 minutes so this is cheap. Preselect the plan named in ?plan=
  // (any case), else the IsPopular plan, else the first selectable
  // (non-custom) plan.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await billingApi.getPlans();
        if (cancelled) return;
        const active = data.filter(p => !p.isCustomPricing);
        setPlans(active);
        const requested = query.plan ? active.find(p => p.name.toLowerCase() === query.plan) : undefined;
        const popular = active.find(p => p.isPopular);
        setSelectedPlanId((requested ?? popular ?? active[0])?.id ?? '');
      } catch (err: any) {
        if (!cancelled) {
          setPlansError(err?.response?.data?.error ?? 'Could not load plans. Please try again.');
        }
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [query]);

  // Prefill email if it was passed in the URL (deep-link from the
  // marketing hero "Start free trial" button).
  useEffect(() => {
    const e = searchParams.get('email');
    if (e) setEmail(e);
  }, [searchParams]);

  const selectedPlan = useMemo(
    () => plans.find(p => p.id === selectedPlanId) ?? null,
    [plans, selectedPlanId],
  );
  // The choice survives switching to a plan without a yearly price and back.
  const effectiveInterval: BillingInterval = offersYearly(selectedPlan) ? billingInterval : 'month';
  const individual = !!selectedPlan?.isSelfServeIndividual;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim()) { setError('Please enter your full name.'); return; }
    if (!email.trim()) { setError(individual ? 'Please enter your email.' : 'Please enter your work email.'); return; }
    if (!selectedPlan) { setError('Please choose a plan to continue.'); return; }
    setSubmitting(true);
    try {
      const { checkoutUrl } = await billingApi.checkout({
        productId: selectedPlan.id,
        email: email.trim(),
        organisationName: company.trim() || (individual ? fullName.trim() : `${fullName.trim()}'s team`),
        executiveName: fullName.trim(),
        seatCount: individual ? 1 : Math.max(1, Math.min(seatCount, selectedPlan.maxExecutiveSeats)),
        interval: effectiveInterval,
      });
      // Enhanced conversions — hand gtag the user-typed email so it can
      // hash it client-side and improve match rates. The email is available
      // in the clear here (the user just typed it); the /onboarding/success
      // page only ever sees a masked copy from the API, so we also stash the
      // normalised address in sessionStorage for the Completed conversion to
      // pick up after the same-tab Stripe round-trip. (Inert until Alex
      // accepts the customer-data terms in the Ads UI.)
      const normalisedEmail = email.trim().toLowerCase();
      try { sessionStorage.setItem('gads_ec_email', normalisedEmail); } catch { /* private mode */ }
      window.gtag?.('set', 'user_data', { email: normalisedEmail });
      // Google Ads — "Trial Signup Started" (secondary; observation +
      // remarketing, does not drive bidding). Fire the moment before we hand
      // off to Stripe. Because the next line navigates away, give the beacon a
      // short window to send via event_callback, capped so we never block the
      // user for more than 800ms.
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => { if (!done) { done = true; resolve(); } };
        window.gtag?.('event', 'conversion', {
          send_to: 'AW-18076346956/5r-eCO_l_tscEMzUvKtD',
          event_callback: finish,
        });
        setTimeout(finish, 800);
      });
      // Hand the user to Stripe — Stripe will redirect to the success
      // page (which is on this origin) once payment is captured, and
      // the webhook will have provisioned the tenant by then.
      window.location.href = checkoutUrl;
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    // The shared sign-in backdrop, so Signup and Login read as one moment.
    <AuthShell width="plans" contentClassName="my-10">
      <div className="mb-5">
        <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
      </div>

      <h1 className={cn('mb-[10px]', AUTH_TITLE_CLASS)}>
        Start your 14&#8209;day trial.
      </h1>
      <p className={cn('mb-[30px]', AUTH_SUBTITLE_CLASS)}>
        Pick a plan to begin. No charges during the trial.
      </p>

      {error && (
        <Alert variant="error" className="mb-3 rounded-xl">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Plan picker — selectable cards. Pre-loaded from the platform
          admin's BillingProduct table; selecting one drives which
          Stripe Price the checkout session uses. */}
      <div className="mb-5">
        <Label className={cn('mb-[7px] block', AUTH_LABEL_CLASS)}>
          Choose a plan
        </Label>
        {plansLoading ? (
          <div className="flex h-[100px] items-center justify-center rounded-[11px] border border-foreground/[0.08] bg-foreground/[0.02]">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : plansError ? (
          <Alert variant="error" className="rounded-xl">
            <AlertDescription>{plansError}</AlertDescription>
          </Alert>
        ) : plans.length === 0 ? (
          <Alert variant="warning" className="rounded-xl">
            <AlertDescription>
              No plans are available right now. Please contact{' '}
              <a href="mailto:sales@stratiumhq.com" className="underline">sales@stratiumhq.com</a>.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="flex flex-col gap-2">
            {plans.map(p => {
              const active = p.id === selectedPlanId;
              const { price, unit } = planPrice(p, active ? effectiveInterval : billingInterval);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedPlanId(p.id)}
                  aria-pressed={active}
                  className={cn(
                    'flex items-start gap-3 rounded-[11px] border px-4 py-3 text-left transition-colors',
                    active
                      ? 'border-primary bg-primary/5'
                      : 'border-foreground/10 bg-foreground/[0.02] hover:border-foreground/25',
                  )}
                >
                  <span
                    className={cn(
                      'mt-1 grid h-4 w-4 shrink-0 place-items-center rounded-full border-2',
                      active ? 'border-primary bg-primary' : 'border-foreground/30',
                    )}
                  >
                    {active && <Check className="h-2.5 w-2.5 text-ink" strokeWidth={3} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-base font-semibold text-foreground">
                        {p.name}
                        {p.isPopular && (
                          <span className="ml-2 rounded-full bg-primary/15 px-2 py-[1px] text-[10px] font-medium uppercase tracking-wide text-primary">
                            Popular
                          </span>
                        )}
                      </span>
                      <span className="text-sm font-medium text-foreground/80">
                        {price}
                        <span className="text-xs font-normal text-muted-foreground">
                          {unit}
                        </span>
                      </span>
                    </div>
                    {p.description && (
                      <p className="mt-1 text-xs leading-snug text-muted-foreground">
                        {p.description}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Monthly / yearly — only for a plan with a yearly price. */}
      {offersYearly(selectedPlan) && (
        <div className="mb-5">
          <Label className={cn('mb-[7px] block', AUTH_LABEL_CLASS)}>
            Billing
          </Label>
          <div role="radiogroup" aria-label="Billing interval" className="grid grid-cols-2 gap-2">
            {(['month', 'year'] as const).map(iv => {
              const on = effectiveInterval === iv;
              return (
                <button
                  key={iv}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setBillingInterval(iv)}
                  className={cn(
                    'rounded-[11px] border px-3 py-2.5 text-left transition-colors',
                    on
                      ? 'border-primary bg-primary/5'
                      : 'border-foreground/10 bg-foreground/[0.02] hover:border-foreground/25',
                  )}
                >
                  <span className="block text-sm font-semibold text-foreground">{iv === 'month' ? 'Monthly' : 'Yearly'}</span>
                  <span className="block text-xs leading-snug text-muted-foreground">
                    {intervalCopy(selectedPlan, iv)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-[14px]">
        <div className="flex flex-col gap-[7px]">
          <Label htmlFor="signup-name" className={AUTH_LABEL_CLASS}>
            Full name
          </Label>
          <Input
            id="signup-name"
            type="text"
            autoComplete="name"
            required
            autoFocus
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Alex Morgan"
            className={AUTH_FIELD_CLASS}
          />
        </div>

        <div className="flex flex-col gap-[7px]">
          <Label htmlFor="signup-email" className={AUTH_LABEL_CLASS}>
            {individual ? 'Email' : 'Work email'}
          </Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => { setEmail(e.target.value); setEmailHint(''); }}
            onBlur={() => {
              if (email && !individual && !isWorkEmailish(email)) {
                setEmailHint('A work email helps us connect your calendar properly.');
              }
            }}
            placeholder="you@company.com"
            className={AUTH_FIELD_CLASS}
            aria-describedby={emailHint ? 'signup-email-hint' : undefined}
          />
          {emailHint && (
            <p id="signup-email-hint" className="text-xs text-rose-400">
              {emailHint}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-[7px]">
          <Label htmlFor="signup-company" className={AUTH_LABEL_CLASS}>
            Company <span className="text-muted-foreground/70">(optional)</span>
          </Label>
          <Input
            id="signup-company"
            type="text"
            autoComplete="organization"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Company name"
            className={AUTH_FIELD_CLASS}
          />
        </div>

        {selectedPlan && !selectedPlan.isCustomPricing && !individual && (
          <div className="flex flex-col gap-[7px]">
            <Label htmlFor="signup-seats" className={AUTH_LABEL_CLASS}>
              Executive seats
            </Label>
            <Input
              id="signup-seats"
              type="number"
              min={1}
              max={selectedPlan.maxExecutiveSeats}
              value={seatCount}
              onChange={(e) => setSeatCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className={AUTH_FIELD_CLASS}
            />
            <p className="text-xs text-muted-foreground">
              Up to {selectedPlan.maxExecutiveSeats} seats on {selectedPlan.name}. You won't be charged until your trial ends.
            </p>
          </div>
        )}

        <Button
          type="submit"
          disabled={submitting || plansLoading || !selectedPlan}
          className={cn('mt-[6px] gap-2', AUTH_PRIMARY_BUTTON)}
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          Continue to payment
        </Button>
      </form>

      <p className="mt-5 text-center text-xs leading-relaxed text-muted-foreground">
        By starting your trial you agree to our{' '}
        <a href="https://www.stratiumhq.com/terms" target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:text-primary">Terms</a>
        {' '}and{' '}
        <a href="https://www.stratiumhq.com/privacy" target="_blank" rel="noopener noreferrer" className="text-foreground underline underline-offset-2 hover:text-primary">Privacy Policy</a>.
        You will not be charged during your 14-day trial.
      </p>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => { window.location.href = '/login'; }}
          className="font-medium text-foreground underline-offset-4 hover:underline"
        >
          Sign in
        </button>
      </p>

      <p className={cn('mt-8 text-center', AUTH_FOOTNOTE_CLASS)}>
        Cadence is the product. Stratium is the company behind it.
      </p>
    </AuthShell>
  );
}
